import type { FormEvent } from 'react';
import firebase from 'firebase/compat/app';
import { Icon } from '../Icon';
import { Toast } from '../modals/Toast';
import type { OrgTeam } from '../../types';

interface LoginScreenProps {
    firebaseUser: firebase.User | null;
    loginName: string;
    setLoginName: (v: string) => void;
    loginPassword: string;
    setLoginPassword: (v: string) => void;
    loginError: string;
    isAuthBusy: boolean;
    handlePasswordLogin: (e: FormEvent) => void;
    handleRegisterAndLogin: (e: FormEvent) => void;
    newMemberName: string;
    setNewMemberName: (v: string) => void;
    newMemberOrg: string;
    setNewMemberOrg: (v: string) => void;
    newMemberTeam: string;
    setNewMemberTeam: (v: string) => void;
    newMemberDept: string;
    setNewMemberDept: (v: string) => void;
    orgTeamList: OrgTeam[];
    newMemberPassword: string;
    setNewMemberPassword: (v: string) => void;
    newMemberPasswordConfirm: string;
    setNewMemberPasswordConfirm: (v: string) => void;
    toastMessage: string | null;
    publicRegistrationOpen: boolean;
}

export function LoginScreen({
    firebaseUser,
    loginName,
    setLoginName,
    loginPassword,
    setLoginPassword,
    loginError,
    isAuthBusy,
    handlePasswordLogin,
    handleRegisterAndLogin,
    newMemberName,
    setNewMemberName,
    newMemberOrg,
    setNewMemberOrg,
    newMemberTeam,
    setNewMemberTeam,
    newMemberDept,
    setNewMemberDept,
    orgTeamList,
    newMemberPassword,
    setNewMemberPassword,
    newMemberPasswordConfirm,
    setNewMemberPasswordConfirm,
    toastMessage,
    publicRegistrationOpen,
}: LoginScreenProps) {
    return (
        <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
            <div className="max-w-4xl w-full bg-white rounded-3xl shadow-2xl overflow-hidden border border-slate-200 flex flex-col md:flex-row">
                <div className="md:w-5/12 bg-gradient-to-br from-indigo-800 to-indigo-950 p-8 flex flex-col justify-between text-white">
                    <div className="space-y-4">
                        <div className="bg-indigo-500 w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg select-none">
                            <Icon name="award" className="w-7 h-7 text-white" />
                        </div>
                        <h2 className="text-2xl font-bold tracking-tight leading-tight mt-6">
                            개인 평가<br />제도 개선 디지털 포털
                        </h2>
                        <p className="text-xs text-indigo-100 leading-relaxed pt-2">
                            3단계 절대평가(EX, GD, NI), 연중 분기별 상시 성과 피드백, 그리고 협업 기반 다면평가를 한눈에 모니터링하고 제어하는 기업 통합 성과 플랫폼입니다.
                        </p>
                    </div>
                    <div className="text-[10px] text-indigo-300 mt-12 md:mt-0 font-semibold">
                        데이터베이스 상태: <span className={firebaseUser ? "text-emerald-400 font-bold" : "text-amber-400 font-bold"}>
                            {firebaseUser ? 'Cloud Firestore 실시간 연동중' : '서버 연결 중...'}
                        </span>
                    </div>
                </div>

                <div className="md:w-7/12 p-8 bg-white flex flex-col justify-center">
                    <h3 className="text-slate-900 font-bold text-lg mb-1 flex items-center gap-2">
                        <Icon name="key" className="text-indigo-600 w-5 h-5" /> 인사 포털 인증 로그인
                    </h3>
                    <p className="text-xs text-slate-500 mb-6">등록된 임직원 성명과 비밀번호를 입력하여 로그인하십시오.</p>

                    <form onSubmit={handlePasswordLogin} className="space-y-3 text-xs">
                        <div>
                            <label className="block text-slate-600 font-bold mb-1">임직원 성명</label>
                            <input
                                type="text"
                                required
                                value={loginName}
                                onChange={(e) => setLoginName(e.target.value)}
                                placeholder="성함 입력"
                                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 font-semibold"
                            />
                        </div>
                        <div>
                            <label className="block text-slate-600 font-bold mb-1">비밀번호</label>
                            <input
                                type="password"
                                required
                                value={loginPassword}
                                onChange={(e) => setLoginPassword(e.target.value)}
                                placeholder="비밀번호 입력"
                                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 font-semibold"
                            />
                        </div>
                        {loginError && (
                            <p className="text-rose-600 font-semibold text-[11px]">{loginError}</p>
                        )}
                        <button
                            type="submit"
                            disabled={isAuthBusy || !firebaseUser}
                            className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-300 text-white font-bold py-2.5 rounded-xl transition-all shadow-md mt-1"
                        >
                            {isAuthBusy ? '확인 중...' : '로그인'}
                        </button>
                    </form>

                    {publicRegistrationOpen ? (
                        <>
                    <div className="relative flex py-2 items-center mt-5">
                        <div className="flex-grow border-t border-slate-200"></div>
                        <span className="flex-shrink mx-4 text-slate-400 text-[10px] font-bold">또는 신규 가입 후 로그인</span>
                        <div className="flex-grow border-t border-slate-200"></div>
                    </div>

                    <form onSubmit={handleRegisterAndLogin} className="space-y-3 text-xs mt-3">
                        <div>
                            <label className="block text-slate-600 font-bold mb-1">임직원 성명</label>
                            <input
                                type="text"
                                required
                                value={newMemberName}
                                onChange={(e) => setNewMemberName(e.target.value)}
                                placeholder="성함 입력"
                                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 font-semibold"
                            />
                        </div>
                        <p className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 leading-relaxed">
                            공개 가입은 <span className="font-bold text-indigo-700">팀원</span>만 가능합니다.
                            파트장·팀장·담당자 계정은 인사담당자가 HR 어드민에서 생성합니다.
                        </p>

                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="block text-slate-600 font-bold mb-1">소속 조직</label>
                                <select
                                    value={newMemberOrg}
                                    onChange={(e) => {
                                        const org = e.target.value;
                                        setNewMemberOrg(org);
                                        const firstTeam = orgTeamList.find(d => d.org === org);
                                        const team = firstTeam ? firstTeam.team : '';
                                        setNewMemberTeam(team);
                                        setNewMemberDept(team ? `${org} ${team}` : '');
                                    }}
                                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 font-semibold cursor-pointer"
                                >
                                    {[...new Set(orgTeamList.map(d => d.org))].map(org => (
                                        <option key={org} value={org}>{org}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-slate-600 font-bold mb-1">소속 팀</label>
                                <select
                                    value={newMemberTeam}
                                    onChange={(e) => {
                                        const team = e.target.value;
                                        setNewMemberTeam(team);
                                        setNewMemberDept(newMemberOrg ? `${newMemberOrg} ${team}` : '');
                                    }}
                                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 font-semibold cursor-pointer"
                                >
                                    {orgTeamList.filter(d => d.org === newMemberOrg).map(d => (
                                        <option key={d.team} value={d.team}>{d.team}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <p className="text-[10px] text-slate-400">
                            선택된 소속: <span className="font-bold text-indigo-600">{newMemberDept || '미선택'}</span>
                        </p>

                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="block text-slate-600 font-bold mb-1">비밀번호 설정</label>
                                <input
                                    type="password"
                                    required
                                    value={newMemberPassword}
                                    onChange={(e) => setNewMemberPassword(e.target.value)}
                                    placeholder="8자 이상"
                                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 font-semibold"
                                />
                            </div>
                            <div>
                                <label className="block text-slate-600 font-bold mb-1">비밀번호 확인</label>
                                <input
                                    type="password"
                                    required
                                    value={newMemberPasswordConfirm}
                                    onChange={(e) => setNewMemberPasswordConfirm(e.target.value)}
                                    placeholder="다시 입력"
                                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 font-semibold"
                                />
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={isAuthBusy || !firebaseUser}
                            className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-300 text-white font-bold py-2.5 rounded-xl transition-all shadow-md mt-2"
                        >
                            팀원으로 가입하기
                        </button>
                    </form>
                        </>
                    ) : (
                        <p className="text-[11px] text-slate-500 bg-amber-50 border border-amber-100 rounded-xl px-3 py-2 mt-5 leading-relaxed">
                            현재 <span className="font-bold text-amber-800">공개 가입이 닫혀</span> 있습니다.
                            계정은 인사담당자(HR 어드민)가 발급합니다.
                        </p>
                    )}
                </div>
            </div>

            <Toast message={toastMessage} />
        </div>
    );
}
